/* Reusable voice-note recorder + uploader.
   - Press-and-hold (or click toggle) to record up to 3 minutes.
   - Animated waveform of the live volume.
   - On stop, previews the note and offers Cancel / Send.

   Props:
     recipientId: string
     onSent: (voiceNoteDoc) => void
     compact?: boolean          (smaller pill UI)
     attachedToLetterId?: string
*/
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Square, Trash2, Send, Pause, Play } from 'lucide-react';

const MAX_SECONDS = 180; // 3 minutes
const BASE = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtTime(s) {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export default function VoiceRecorder({ recipientId, onSent, compact = false, attachedToLetterId, accent = 'var(--codex-gold)' }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [meterLevel, setMeterLevel] = useState(0); // 0..1
  const [previewing, setPreviewing] = useState(false);

  const mediaRecorder = useRef(null);
  const chunks = useRef([]);
  const stream = useRef(null);
  const audioCtx = useRef(null);
  const analyser = useRef(null);
  const meterRaf = useRef(null);
  const startTime = useRef(0);
  const tickInterval = useRef(null);
  const audioEl = useRef(null);

  const cleanupTracks = () => {
    if (stream.current) {
      stream.current.getTracks().forEach((t) => t.stop());
      stream.current = null;
    }
    if (meterRaf.current) cancelAnimationFrame(meterRaf.current);
    if (audioCtx.current) {
      try { audioCtx.current.close(); } catch (e) { /* ignore */ }
      audioCtx.current = null;
    }
    if (tickInterval.current) {
      clearInterval(tickInterval.current);
      tickInterval.current = null;
    }
  };

  useEffect(() => () => {
    cleanupTracks();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const startRecording = async () => {
    setError('');
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = s;
      chunks.current = [];
      const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
      const mime = types.find((t) => window.MediaRecorder?.isTypeSupported(t)) || '';
      const mr = new MediaRecorder(s, mime ? { mimeType: mime } : undefined);
      mediaRecorder.current = mr;
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.current.push(e.data); };
      mr.onstop = () => {
        const finalBlob = new Blob(chunks.current, { type: mr.mimeType || 'audio/webm' });
        setBlob(finalBlob);
        setPreviewUrl(URL.createObjectURL(finalBlob));
        cleanupTracks();
      };
      mr.start(250);

      // Volume analyser for waveform animation
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtx.current = ctx;
      const src = ctx.createMediaStreamSource(s);
      const an = ctx.createAnalyser();
      an.fftSize = 256;
      src.connect(an);
      analyser.current = an;
      const data = new Uint8Array(an.frequencyBinCount);
      const tick = () => {
        an.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length / 255;
        setMeterLevel(avg);
        meterRaf.current = requestAnimationFrame(tick);
      };
      tick();

      startTime.current = Date.now();
      setSeconds(0);
      tickInterval.current = setInterval(() => {
        const elapsed = (Date.now() - startTime.current) / 1000;
        if (elapsed >= MAX_SECONDS) {
          stopRecording();
          return;
        }
        setSeconds(elapsed);
      }, 200);
      setRecording(true);
    } catch (e) {
      setError(e.message || 'Microphone unavailable. Allow access in browser settings.');
    }
  };

  const stopRecording = () => {
    setRecording(false);
    try { mediaRecorder.current?.stop(); } catch (e) { /* ignore */ }
  };

  const cancelPreview = () => {
    setBlob(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewing(false);
    setSeconds(0);
  };

  const togglePlay = () => {
    if (!audioEl.current) return;
    if (audioEl.current.paused) audioEl.current.play();
    else audioEl.current.pause();
  };

  const send = async () => {
    if (!blob || busy) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('recipient_id', recipientId);
      fd.append('duration_seconds', Math.max(1, Math.round(seconds)));
      fd.append('file', blob, 'voice.webm');
      if (attachedToLetterId) fd.append('attached_to_letter_id', attachedToLetterId);
      const res = await fetch(`${BASE}/api/codex/voice/send`, {
        method: 'POST',
        headers: { ...authHeader() },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      const json = await res.json();
      cancelPreview();
      if (onSent) onSent(json);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  // ===== UI =====
  if (blob && previewUrl) {
    return (
      <div
        data-testid="voice-recorder-preview"
        className="codex-card"
        style={{
          padding: compact ? 10 : 14,
          display: 'flex', alignItems: 'center', gap: 12,
          borderColor: 'rgba(212,175,55,0.4)',
        }}
      >
        <button
          type="button"
          onClick={togglePlay}
          className="codex-btn codex-btn-ghost"
          data-testid="voice-preview-play"
          style={{ padding: 8, minWidth: 36 }}
        >
          {previewing ? <Pause size={14} /> : <Play size={14} />}
        </button>
        <audio
          ref={audioEl}
          src={previewUrl}
          onPlay={() => setPreviewing(true)}
          onPause={() => setPreviewing(false)}
          onEnded={() => setPreviewing(false)}
          style={{ display: 'none' }}
        />
        <div style={{ flex: 1, fontSize: 13, color: 'var(--codex-gold-light)' }}>
          🎙 {fmtTime(seconds)} <span style={{ opacity: 0.7 }}>· ready to send</span>
        </div>
        <button
          type="button"
          onClick={cancelPreview}
          disabled={busy}
          className="codex-btn codex-btn-ghost"
          data-testid="voice-preview-cancel"
          style={{ padding: '6px 10px', fontSize: 11 }}
        >
          <Trash2 size={12} />
        </button>
        <button
          type="button"
          onClick={send}
          disabled={busy}
          data-testid="voice-preview-send"
          className="codex-btn codex-btn-primary"
          style={{ padding: '6px 12px', fontSize: 11 }}
        >
          <Send size={12} /> {busy ? 'Sending…' : 'Send'}
        </button>
        {error && <div style={{ color: '#fca5a5', fontSize: 11 }}>{error}</div>}
      </div>
    );
  }

  return (
    <div data-testid="voice-recorder" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <button
        type="button"
        onClick={recording ? stopRecording : startRecording}
        data-testid={recording ? 'voice-recorder-stop' : 'voice-recorder-start'}
        className="codex-btn"
        style={{
          padding: compact ? '8px 14px' : '10px 18px',
          background: recording ? 'rgba(239,68,68,0.15)' : 'rgba(245,217,124,0.06)',
          color: recording ? '#fca5a5' : 'var(--codex-gold-light)',
          border: `1px solid ${recording ? 'rgba(239,68,68,0.55)' : 'rgba(245,217,124,0.25)'}`,
        }}
      >
        {recording ? <Square size={14} /> : <Mic size={14} />}
        {recording ? 'Stop' : 'Record voice note'}
      </button>

      <AnimatePresence>
        {recording && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {/* Live waveform pulse */}
            <div style={{ display: 'flex', gap: 3, alignItems: 'center', height: 22 }}>
              {Array.from({ length: 9 }).map((_, i) => {
                const h = Math.max(4, 22 * (0.4 + 0.6 * meterLevel) * (0.5 + 0.5 * Math.sin((Date.now() / 220) + i)));
                return (
                  <div
                    key={i}
                    style={{
                      width: 3, height: `${Math.min(22, h)}px`,
                      background: accent, borderRadius: 2,
                      transition: 'height 0.05s linear',
                      opacity: 0.85,
                    }}
                  />
                );
              })}
            </div>
            <span style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.1em', fontSize: 12, color: '#fca5a5' }}>
              REC · {fmtTime(seconds)} / {fmtTime(MAX_SECONDS)}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {error && <div style={{ color: '#fca5a5', fontSize: 12, marginLeft: 8 }}>{error}</div>}
    </div>
  );
}
